'use client'

import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import { Button } from '@planner/ui'
import { ArrowRight, Lightbulb, Rocket, CheckCircle2 } from 'lucide-react'
import { DeviceMockup } from './DeviceMockup'
import { AnimatedSection } from './AnimatedSection'
import { motion } from 'framer-motion'

export function HeroSection() {
  const { t } = useTranslation()

  const benefits = [
    {
      icon: Lightbulb,
      text: t('landing.hero.benefit1'),
    },
    {
      icon: Rocket,
      text: t('landing.hero.benefit2'),
    },
    {
      icon: CheckCircle2,
      text: t('landing.hero.benefit3'),
    },
  ]

  return (
    <section className="relative bg-gradient-to-b from-background via-green-50/20 dark:via-green-950/10 to-background py-16 md:py-24 lg:py-32 overflow-x-hidden">
      <div className="container px-4 sm:px-6 relative z-0 max-w-full">
        <div className="grid lg:grid-cols-[auto_1fr] gap-8 lg:gap-12 items-start max-w-7xl mx-auto">
          {/* Left: Device Mockup - Smaller and positioned higher */}
          <AnimatedSection direction="right" className="hidden lg:block order-2 lg:order-1">
            <div className="relative -mt-8">
              <DeviceMockup />
              {/* Decorative elements around the mockup */}
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                className="absolute -top-8 -right-8 w-32 h-32 bg-green-200/30 dark:bg-green-800/20 rounded-full blur-3xl"
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7, duration: 0.5 }}
                className="absolute -bottom-8 -left-8 w-40 h-40 bg-blue-200/30 dark:bg-blue-800/20 rounded-full blur-3xl"
              />
            </div>
          </AnimatedSection>
          
          {/* Right: Content - More compact with better spacing */}
          <AnimatedSection direction="left" className="text-center lg:text-left order-1 lg:order-2 space-y-8 w-full min-w-0">
            <div className="w-full min-w-0 px-2 sm:px-0">
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-bold tracking-tight text-foreground leading-tight break-words overflow-wrap-anywhere hyphens-auto">
                {t('landing.hero.title')}
              </h1>
              <p className="mt-4 sm:mt-6 text-sm sm:text-base md:text-lg lg:text-xl text-muted-foreground max-w-2xl mx-auto lg:mx-0 break-words overflow-wrap-anywhere">
                {t('landing.hero.subtitle')}
              </p>
            </div>
            
            {/* Benefits - More compact cards */}
            <div className="grid gap-3 sm:grid-cols-1">
              {benefits.map((benefit, index) => {
                const Icon = benefit.icon
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + index * 0.1 }}
                    className="flex items-center gap-3 bg-white/50 dark:bg-gray-900/50 backdrop-blur-sm p-4 rounded-xl border border-green-100 dark:border-green-900/30 hover:border-green-300 dark:hover:border-green-700/50 transition-colors"
                  >
                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-400 to-green-600 dark:from-green-600 dark:to-green-800 flex items-center justify-center flex-shrink-0 shadow-lg shadow-green-500/20"
                    >
                      <Icon className="h-6 w-6 text-white" />
                    </motion.div>
                    <span className="text-sm sm:text-base font-medium text-foreground">{benefit.text}</span>
                  </motion.div>
                )
              })}
            </div>
            
            {/* CTA Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.6 }}
              className="flex flex-col sm:flex-row gap-4 items-center lg:items-start justify-center lg:justify-start pt-4"
            >
              <Link href="/register" className="w-full sm:w-auto">
                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full"
                >
                  <Button 
                    size="lg" 
                    variant="default"
                    className="w-full sm:w-auto !bg-green-600 hover:!bg-green-700 !text-white hover:!text-white shadow-lg shadow-green-600/40 hover:shadow-xl hover:shadow-green-600/50 text-base px-8 py-6 font-semibold transition-all duration-300 border-0 [&::before]:!hidden [&::after]:!hidden hover:[&::before]:!hidden hover:[&::after]:!hidden"
                    style={{
                      background: 'rgb(22, 163, 74)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'rgb(21, 128, 61)'
                      e.currentTarget.style.color = 'white'
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'rgb(22, 163, 74)'
                      e.currentTarget.style.color = 'white'
                    }}
                  >
                    <span className="relative z-10 text-white">{t('landing.hero.freePlanButton')}</span>
                    <ArrowRight className="ml-2 h-5 w-5 relative z-10 text-white" />
                  </Button>
                </motion.div>
              </Link>
              <Link href="#demo" className="w-full sm:w-auto">
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="w-full"
                >
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 text-base px-8 py-6">
                    {t('landing.hero.ctaSecondary')}
                  </Button>
                </motion.div>
              </Link>
            </motion.div>

            {/* Trust indicators */}
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="flex flex-wrap items-center justify-center lg:justify-start gap-6 pt-4 text-sm text-muted-foreground"
            >
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>Darmowy plan na zawsze</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>Wszystko w jednym miejscu</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>Bez ukrytych opłat</span>
              </div>
            </motion.div>
          </AnimatedSection>
        </div>
      </div>
    </section>
  )
}

